/**
 * PurchaseRegisterScreen — every supplier bill in a period with its GST split
 * and per-supplier totals; the books-side purchase record and ITC check.
 *
 * Reuses `loadBooksPurchases` (the same source GSTR-2B reconciles against), so
 * the register and the GST reports agree. Computed entirely from local
 * WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadBooksPurchases } from '@/reports/gst/purchaseDataSource';
import { buildPurchaseRegister, type PurchaseRegister } from '@/reports/purchaseRegister/purchaseRegister';

// ─── Component ───────────────────────────────────────────────────────────────

export function PurchaseRegisterScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<PurchaseRegister | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);
  const [view, setView]             = useState<'bills' | 'suppliers'>('bills');

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const purchases = await loadBooksPurchases(activeStoreId, from, to);
      setData(buildPurchaseRegister(purchases.map((p) => ({
        supplierName: p.supplierName,
        supplierGstin: p.supplierGstin,
        invoiceNumber: p.invoiceNumber,
        // BooksPurchase types date as nullable, but loadBooksPurchases always
        // falls back to the PO's created_at — coerce for the non-null builder.
        date: p.date ?? new Date(),
        taxable: p.taxable,
        cgst: p.cgst,
        sgst: p.sgst,
        igst: p.igst,
        cess: p.cess,
        totalTax: p.totalTax,
        value: p.value,
      }))));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = view === 'bills'
        ? toCsv(data.rows, [
            { header: 'Date', value: (r) => toDateInputValue(r.date) },
            { header: 'Supplier', value: (r) => r.supplierName },
            { header: 'GSTIN', value: (r) => r.supplierGstin ?? '' },
            { header: 'Invoice', value: (r) => r.invoiceNumber },
            { header: 'Taxable', value: (r) => r.taxable },
            { header: 'CGST', value: (r) => r.cgst },
            { header: 'SGST', value: (r) => r.sgst },
            { header: 'IGST', value: (r) => r.igst },
            { header: 'Total', value: (r) => r.value },
            { header: 'ITC', value: (r) => (r.noItc ? 'none' : 'claimable') },
          ])
        : toCsv(data.bySupplier, [
            { header: 'Supplier', value: (s) => s.supplierName },
            { header: 'GSTIN', value: (s) => s.gstin ?? '' },
            { header: 'Bills', value: (s) => s.count },
            { header: 'Taxable', value: (s) => s.taxable },
            { header: 'Tax', value: (s) => s.totalTax },
            { header: 'Total', value: (s) => s.value },
          ]);
      await file.saveText({
        content: csv,
        defaultFileName: `PurchaseRegister_${view}_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildRegisterHtml(data, from, to),
        defaultFileName: `PurchaseRegister_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.rows.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">
          No received purchases in this period.
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Purchases" value={formatCurrency(data.totals.value)} sub={`${data.totals.count} bills`} accent />
            <KPI label="Taxable value" value={formatCurrency(data.totals.taxable)} />
            <KPI label="Claimable ITC" value={formatCurrency(data.claimableItc)} />
            <KPI label="No-ITC purchases" value={formatCurrency(data.noItcValue)} sub={`${data.noItcCount} bills`} warn={data.noItcCount > 0} />
          </div>

          {data.noItcCount > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-4 py-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-ink" />
              <div className="text-body text-fg-secondary">
                {data.noItcCount} bill{data.noItcCount !== 1 ? 's' : ''} ({formatCurrency(data.noItcValue)}) are from suppliers with no GSTIN — no input-tax credit is claimable on them.
              </div>
            </div>
          )}

          {/* View toggle */}
          <div className="flex items-center gap-2">
            {(['bills', 'suppliers'] as const).map((v) => (
              <button key={v} type="button" onClick={() => setView(v)}
                className={cn('rounded-md px-3 py-1 text-body transition-colors',
                  view === v ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
                {v === 'bills' ? 'By bill' : 'By supplier'}
              </button>
            ))}
          </div>

          <Card title={view === 'bills' ? 'Purchase bills' : 'By supplier'}>
            <div className="overflow-x-auto">
              {view === 'bills' ? (
                <table className="w-full min-w-[760px] text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="pb-1 text-left font-normal">Date</th>
                      <th className="pb-1 text-left font-normal">Supplier</th>
                      <th className="pb-1 text-left font-normal">Invoice</th>
                      <th className="pb-1 text-right font-normal">Taxable</th>
                      <th className="pb-1 text-right font-normal">Tax</th>
                      <th className="pb-1 text-right font-normal">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={i} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 text-fg-secondary tabular-nums">{r.date.toLocaleDateString('en-IN')}</td>
                        <td className="py-1.5">
                          <span className="text-fg-primary">{r.supplierName}</span>
                          {r.noItc && <span className="ml-1.5 rounded bg-warning/10 px-1 text-label text-warning-ink">no ITC</span>}
                        </td>
                        <td className="py-1.5 text-fg-secondary tabular-nums">{r.invoiceNumber}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(r.taxable)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(r.totalTax)}</td>
                        <td className="py-1.5 text-right font-medium tabular-nums text-fg-primary">{formatCurrency(r.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-stroke-hairline/20 font-medium">
                      <td className="pt-2 text-fg-primary" colSpan={3}>Total</td>
                      <td className="pt-2 text-right tabular-nums text-fg-primary">{formatCurrency(data.totals.taxable)}</td>
                      <td className="pt-2 text-right tabular-nums text-fg-primary">{formatCurrency(data.totals.totalTax)}</td>
                      <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.totals.value)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <table className="w-full min-w-[640px] text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="pb-1 text-left font-normal">Supplier</th>
                      <th className="pb-1 text-left font-normal">GSTIN</th>
                      <th className="pb-1 text-right font-normal">Bills</th>
                      <th className="pb-1 text-right font-normal">Taxable</th>
                      <th className="pb-1 text-right font-normal">Tax</th>
                      <th className="pb-1 text-right font-normal">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bySupplier.map((s, i) => (
                      <tr key={i} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 text-fg-primary">{s.supplierName}</td>
                        <td className="py-1.5 text-fg-tertiary tabular-nums">{s.gstin ?? <span className="text-warning-ink">—</span>}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{s.count}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(s.taxable)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(s.totalTax)}</td>
                        <td className="py-1.5 text-right font-medium tabular-nums text-fg-primary">{formatCurrency(s.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, sub, accent, warn }: { label: string; value: string; sub?: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
      {sub && <div className="mt-0.5 text-label text-fg-tertiary">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildRegisterHtml(d: PurchaseRegister, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const rows = d.rows.map((r) =>
    `<tr><td>${fmtDate(r.date)}</td><td>${esc(r.supplierName)}${r.noItc ? ' <span style="color:#b45309">(no ITC)</span>' : ''}</td>
     <td>${esc(r.invoiceNumber)}</td>
     <td style="text-align:right">${fmt(r.taxable)}</td><td style="text-align:right">${fmt(r.totalTax)}</td>
     <td style="text-align:right">${fmt(r.value)}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:10px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2),th:nth-child(3),td:nth-child(3){text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Purchase Register</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · ${d.totals.count} bills · Taxable ${fmt(d.totals.taxable)} · Tax ${fmt(d.totals.totalTax)} · Total ${fmt(d.totals.value)} · Claimable ITC ${fmt(d.claimableItc)}</p>
<table>
<thead><tr><th>Date</th><th>Supplier</th><th>Invoice</th><th style="text-align:right">Taxable</th><th style="text-align:right">Tax</th><th style="text-align:right">Total</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td colspan="3">Total</td><td style="text-align:right">${fmt(d.totals.taxable)}</td><td style="text-align:right">${fmt(d.totals.totalTax)}</td><td style="text-align:right">${fmt(d.totals.value)}</td></tr></tfoot>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
